import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatFCFA, mediaSrc, uploadFile, formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BadgeCheck, Trash2, ShieldBan, Megaphone, Pencil, Zap, MessageCircle, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminMetadata from "@/pages/AdminMetadata";
import AdminAnalytics from "@/pages/AdminAnalytics";

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [ads, setAds] = useState([]);

  const load = () => {
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/admin/users").then(r => setUsers(r.data || []));
    api.get("/admin/shops").then(r => setShops(r.data || []));
    api.get("/admin/products").then(r => setProducts(r.data || []));
    api.get("/ads/all").then(r => setAds(r.data || []));
  };
  useEffect(() => { load(); }, []);

  const confirm = (msg) => window.confirm(msg) && window.confirm("Êtes-vous vraiment sûr ? Cette action est irréversible.");

  const verifyShop = async (id, v) => { await api.post(`/admin/shops/${id}/verify`, { verified: v }); toast.success(v ? "Boutique vérifiée" : "Vérification retirée"); load(); };
  const promoteShop = async (id, v) => { await api.post(`/admin/shops/${id}/promote`, { promoted: v }); toast.success("OK"); load(); };
  const banUser = async (id, v) => { await api.post(`/admin/users/${id}/ban`, { banned: v }); toast.success(v ? "Utilisateur banni" : "Débanni"); load(); };
  const delShop = async (id) => { if (!confirm("Supprimer cette boutique et tous ses produits ?")) return; await api.delete(`/admin/shops/${id}`); toast.success("Supprimée"); load(); };
  const delUser = async (id) => { if (!confirm("Supprimer cet utilisateur, sa boutique et ses produits ?")) return; await api.delete(`/admin/users/${id}`); toast.success("Supprimé"); load(); };
  const delProduct = async (id) => { if (!confirm("Supprimer ce produit ?")) return; await api.delete(`/admin/products/${id}`); toast.success("Supprimé"); load(); };
  const toggleAd = async (ad) => { await api.patch(`/ads/${ad.id}`, { active: !ad.active }); load(); };

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4">
      <h1 className="font-[Manrope] font-extrabold text-2xl">Panneau Administrateur</h1>
      {stats && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-2">
          <Stat label="Utilisateurs" value={stats.users} />
          <Stat label="Marchands" value={stats.merchants} />
          <Stat label="Boutiques" value={stats.shops} />
          <Stat label="Vérifiées" value={stats.verified_shops} />
          <Stat label="Produits" value={stats.products} />
          <Stat label="Contacts (total)" value={stats.contacts_total} sub={`${stats.contacts_today} aujourd'hui · ${stats.contacts_7d} (7j) · ${stats.contacts_30d} (30j)`} highlight />
        </div>
      )}

      <Tabs defaultValue="moderation" className="mt-6">
        <TabsList className="bg-[#F3F1EC] rounded-xl p-1 overflow-x-auto flex">
          <TabsTrigger value="moderation" data-testid="admin-tab-moderation" className="rounded-lg data-[state=active]:bg-white">Modération</TabsTrigger>
          <TabsTrigger value="ads" data-testid="admin-tab-ads" className="rounded-lg data-[state=active]:bg-white">Publicités</TabsTrigger>
          <TabsTrigger value="search" data-testid="admin-tab-search" className="rounded-lg data-[state=active]:bg-white">Recherche</TabsTrigger>
          <TabsTrigger value="broadcast" data-testid="admin-tab-broadcast" className="rounded-lg data-[state=active]:bg-white">Diffusion</TabsTrigger>
          <TabsTrigger value="top" data-testid="admin-tab-top" className="rounded-lg data-[state=active]:bg-white">Analyses & Top listes</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="admin-tab-verifications" className="rounded-lg data-[state=active]:bg-white">Vérifications</TabsTrigger>
          <TabsTrigger value="metadata" data-testid="admin-tab-metadata" className="rounded-lg data-[state=active]:bg-white">Métadonnées</TabsTrigger>
        </TabsList>

        <TabsContent value="metadata" className="mt-4">
          <AdminMetadata />
        </TabsContent>

        <TabsContent value="ads" className="mt-4">
          <AdsManager ads={ads} reload={load} />
        </TabsContent>

        <TabsContent value="moderation" className="mt-4">
          <ModerationPanel />
        </TabsContent>

        <TabsContent value="search" className="mt-4">
          <AdminSearch />
        </TabsContent>

        <TabsContent value="broadcast" className="mt-4">
          <AdminBroadcast />
        </TabsContent>

        <TabsContent value="top" className="mt-4">
          <AdminAnalytics />
          <div className="my-6 border-t border-[#E8E6E1]" />
          <AdminTopLists />
        </TabsContent>

        <TabsContent value="verifications" className="mt-4">
          <VerificationsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, sub, highlight }) {
  return (
    <div className={`p-3 rounded-xl border ${highlight ? "bg-[#FFF3E0] border-[#E65100]/40" : "bg-white border-[#E8E6E1]"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#73706B] font-bold">{label}</div>
      <div className={`mt-1 font-[Manrope] font-extrabold text-xl ${highlight ? "text-[#E65100]" : "text-[#1A1918]"}`}>{value ?? 0}</div>
      {sub && <div className="text-[10px] text-[#73706B] mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Row({ children }) {
  return <div className="p-2.5 rounded-xl border border-[#E8E6E1] bg-white flex items-center gap-2">{children}</div>;
}

function AdsManager({ ads, reload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", subtitle: "", image: "", link: "", type: "banner", tier: "none", priority: "normal", frequency: 6, placement: "all", active: true, order: 0, starts_at: "", ends_at: "", product_id: "", shop_id: "" });

  const openNew = () => { setEditing({}); setForm({ title: "", subtitle: "", image: "", link: "", type: "inline", tier: "none", priority: "normal", frequency: 6, placement: "all", active: true, order: 0, starts_at: "", ends_at: "", product_id: "", shop_id: "" }); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a, tier: a.tier || "none", priority: a.priority || "normal", frequency: a.frequency || 6, placement: a.placement || "all", starts_at: a.starts_at || "", ends_at: a.ends_at || "", product_id: a.product_id || "", shop_id: a.shop_id || "" }); };
  const close = () => setEditing(null);

  const onImage = async (file) => {
    if (!file) return;
    try {
      const url = await uploadFile(file, "ad_image");
      setForm(f => ({ ...f, image: url }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Échec de l'envoi");
    }
  };

  const save = async () => {
    try {
      const payload = { ...form };
      // Normalize datetimes: convert "YYYY-MM-DDTHH:mm" (local) -> ISO or null
      ["starts_at", "ends_at"].forEach(k => {
        if (!payload[k]) payload[k] = null;
        else payload[k] = new Date(payload[k]).toISOString();
      });
      ["product_id", "shop_id"].forEach(k => { if (!payload[k]) payload[k] = null; });
      if (editing?.id) await api.patch(`/ads/${editing.id}`, payload);
      else await api.post("/ads", payload);
      toast.success("Publicité enregistrée");
      close(); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const toggleAd = async (a) => { await api.patch(`/ads/${a.id}`, { active: !a.active }); reload(); };
  const delAd = async (a) => { if (!window.confirm("Supprimer cette publicité ?")) return; await api.delete(`/ads/${a.id}`); toast.success("Supprimée"); reload(); };

  const typeLabel = { banner: "Bannière", inline: "Feed", scroll: "Scroll", shop_promo: "Promo boutique", sponsored_product: "Produit sponsorisé" };
  const tierLabel = { none: "Standard", boost: "Boost x1.5", sponsor: "Sponsor x2", premium: "Premium x3" };
  const tierColor = { none: "bg-[#F3F1EC] text-[#73706B]", boost: "bg-[#FFF3E0] text-[#E65100]", sponsor: "bg-[#1A1918] text-white", premium: "bg-[#7B1FA2] text-white" };
  const priorityLabel = { normal: "Normal x1", important: "Important x2", premium: "Premium x3", sponsor: "Sponsor x4" };
  const priorityColor = { normal: "bg-[#F3F1EC] text-[#73706B]", important: "bg-[#E0F2FE] text-[#075985]", premium: "bg-[#FAE8FF] text-[#7B1FA2]", sponsor: "bg-[#FEF3C7] text-[#92400E]" };

  // Format ISO string to value accepted by <input type=datetime-local> (YYYY-MM-DDTHH:mm)
  const toLocalInput = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm text-[#73706B]">{ads.length} publicité{ads.length > 1 ? "s" : ""}</div>
        <Button onClick={openNew} className="h-10 rounded-xl bg-[#E65100] hover:bg-[#C44100]" data-testid="create-ad-btn">+ Nouvelle pub</Button>
      </div>

      {ads.map(a => (
        <Row key={a.id}>
          <div className="w-16 h-10 rounded bg-[#F3F1EC] overflow-hidden shrink-0">{a.image && <img src={mediaSrc(a.image)} alt="" className="w-full h-full object-cover" />}</div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm truncate flex items-center gap-2 flex-wrap">
              {a.title}
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFF3E0] text-[#E65100]">{typeLabel[a.type] || "Bannière"}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${tierColor[a.tier || "none"]}`}>{tierLabel[a.tier || "none"]}</span>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${priorityColor[a.priority || "normal"]}`}>★ {priorityLabel[a.priority || "normal"]}</span>
              {!a.active && <span className="text-[10px] font-semibold text-[#73706B]">(désactivée)</span>}
            </div>
            <div className="text-xs text-[#73706B] truncate">
              {a.link} · {a.impressions || 0} vues · {a.clicks || 0} clics · CTR {a.impressions ? ((100 * (a.clicks || 0) / a.impressions).toFixed(1) + "%") : "—"} · Fréq. {a.frequency || 6}
              {a.starts_at ? ` · du ${new Date(a.starts_at).toLocaleDateString()}` : ""}{a.ends_at ? ` au ${new Date(a.ends_at).toLocaleDateString()}` : ""}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-ad-${a.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => toggleAd(a)} data-testid={`toggle-ad-${a.id}`}>{a.active ? "Off" : "On"}</Button>
          <Button size="sm" variant="outline" onClick={() => delAd(a)} className="text-[#D32F2F]" data-testid={`del-ad-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
        </Row>
      ))}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && close()}>
        <DialogContent className="sm:max-w-md rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifier" : "Créer"} une publicité</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm(f => ({ ...f, type: v }))}>
                <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="ad-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="banner">Bannière en haut (rotation 3 pubs × 5s)</SelectItem>
                  <SelectItem value="inline">Dans le feed (style Leboncoin)</SelectItem>
                  <SelectItem value="scroll">Scroll (popup au défilement)</SelectItem>
                  <SelectItem value="shop_promo">Promotion boutique (tuile sponsorisée)</SelectItem>
                  <SelectItem value="sponsored_product">Produit sponsorisé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Niveau (boost classement)</Label>
              <Select value={form.tier} onValueChange={(v) => setForm(f => ({ ...f, tier: v }))}>
                <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="ad-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Standard (x1) — affichage seul</SelectItem>
                  <SelectItem value="boost">Boost (x1.5) — petit coup de pouce</SelectItem>
                  <SelectItem value="sponsor">Sponsor (x2) — mise en avant</SelectItem>
                  <SelectItem value="premium">Premium (x3) — top des classements</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorité d'affichage (rotation équitable)</Label>
              <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger className="h-12 rounded-xl mt-1" data-testid="ad-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal (poids x1) — apparaît parfois</SelectItem>
                  <SelectItem value="important">Important (poids x2) — apparaît plus souvent</SelectItem>
                  <SelectItem value="premium">Premium (poids x3) — apparaît fréquemment</SelectItem>
                  <SelectItem value="sponsor">Sponsor (poids x4) — apparaît très souvent</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-[#73706B] mt-1">Plus la priorité est élevée, plus la pub apparaît. Une pénalité d'impressions garantit que toutes les pubs gardent leur chance.</div>
            </div>
            <div>
              <Label>Titre *</Label>
              <Input data-testid="ad-title" className="h-12 rounded-xl mt-1" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Input data-testid="ad-subtitle" className="h-12 rounded-xl mt-1" value={form.subtitle || ""} onChange={(e) => setForm(f => ({ ...f, subtitle: e.target.value }))} placeholder="Court slogan ou argument de vente" />
            </div>
            <div>
              <Label>Image (URL ou upload) *</Label>
              <Input data-testid="ad-image-url" className="h-11 rounded-xl mt-1" placeholder="https://..." value={form.image?.startsWith("data:") ? "" : (form.image || "")} onChange={(e) => setForm(f => ({ ...f, image: e.target.value }))} />
              <input type="file" accept="image/*" className="mt-2 text-xs" onChange={(e) => onImage(e.target.files?.[0])} data-testid="ad-image-file" />
              {form.image && <img src={mediaSrc(form.image)} alt="" className="mt-2 max-h-32 rounded-lg object-cover" />}
            </div>
            <div>
              <Label>Lien de destination *</Label>
              <Input data-testid="ad-link" className="h-12 rounded-xl mt-1" value={form.link} onChange={(e) => setForm(f => ({ ...f, link: e.target.value }))} placeholder="https://..." />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Placement</Label>
                <Select value={form.placement} onValueChange={(v) => setForm(f => ({ ...f, placement: v }))}>
                  <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="ad-placement"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Partout</SelectItem>
                    <SelectItem value="home">Accueil</SelectItem>
                    <SelectItem value="search">Recherche</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fréquence (tous les N produits)</Label>
                <Input data-testid="ad-frequency" type="number" min={3} max={20} className="h-11 rounded-xl mt-1" value={form.frequency} onChange={(e) => setForm(f => ({ ...f, frequency: parseInt(e.target.value || 6) }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Début (optionnel)</Label>
                <Input data-testid="ad-starts-at" type="datetime-local" className="h-11 rounded-xl mt-1" value={toLocalInput(form.starts_at)} onChange={(e) => setForm(f => ({ ...f, starts_at: e.target.value }))} />
              </div>
              <div>
                <Label>Fin (optionnel)</Label>
                <Input data-testid="ad-ends-at" type="datetime-local" className="h-11 rounded-xl mt-1" value={toLocalInput(form.ends_at)} onChange={(e) => setForm(f => ({ ...f, ends_at: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>ID produit lié (optionnel)</Label>
                <Input data-testid="ad-product-id" className="h-11 rounded-xl mt-1" value={form.product_id || ""} onChange={(e) => setForm(f => ({ ...f, product_id: e.target.value }))} placeholder="uuid du produit à booster" />
              </div>
              <div>
                <Label>ID boutique liée (optionnel)</Label>
                <Input data-testid="ad-shop-id" className="h-11 rounded-xl mt-1" value={form.shop_id || ""} onChange={(e) => setForm(f => ({ ...f, shop_id: e.target.value }))} placeholder="uuid de la boutique à booster" />
              </div>
            </div>
            <div className="text-[11px] text-[#73706B] -mt-1">Le multiplicateur du niveau s'applique automatiquement au produit ou à la boutique lié(e).</div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="w-full h-12 rounded-xl bg-[#E65100] hover:bg-[#C44100]" data-testid="save-ad-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function VerificationsManager() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/verifications", { params }).then(r => setItems(r.data || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id) => {
    if (!window.confirm("Approuver cette demande et marquer la boutique vérifiée ?")) return;
    try {
      await api.post(`/admin/verifications/${id}/approve`);
      toast.success("Boutique vérifiée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const reject = async () => {
    if (!rejecting) return;
    try {
      await api.post(`/admin/verifications/${rejecting.id}/reject`, { reason });
      toast.success("Demande refusée");
      setRejecting(null); setReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const statusColor = {
    pending: "bg-[#FFF3E0] text-[#E65100]",
    approved: "bg-[#E6F4EA] text-[#0A7D3A]",
    rejected: "bg-[#FFEAEA] text-[#D32F2F]",
  };
  const statusLabel = { pending: "En attente", approved: "Approuvée", rejected: "Refusée" };

  return (
    <div>
      <div className="flex gap-2 mb-3">
        {["pending", "approved", "rejected", "all"].map(s => (
          <button
            key={s}
            data-testid={`verif-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`h-9 px-3 rounded-lg text-xs font-bold transition border ${filter === s ? "bg-[#E65100] text-white border-[#E65100]" : "bg-white border-[#E8E6E1]"}`}
          >
            {s === "all" ? "Toutes" : statusLabel[s]}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {items.map(r => (
          <Row key={r.id}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate flex items-center gap-2 flex-wrap">
                {r.shop_id ? (
                  <Link
                    to={`/boutique/${r.shop_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`verif-shop-link-${r.id}`}
                    className="text-[#E65100] hover:underline inline-flex items-center gap-1"
                  >
                    {r.shop_name}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : r.shop_name}
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${statusColor[r.status] || ""}`}>{statusLabel[r.status] || r.status}</span>
              </div>
              <div className="text-xs text-[#73706B] mt-0.5">
                {r.owner_name?.trim() || "—"} · {r.owner_email || "—"} · {r.city || "—"}
              </div>
              <div className="text-xs text-[#73706B]">
                WhatsApp : {r.whatsapp || "—"} · Tél : {r.phone || "—"}
              </div>
              <div className="text-[11px] text-[#73706B]">Demandée le {new Date(r.created_at).toLocaleString()}</div>
              {r.rejection_reason && <div className="text-[11px] text-[#D32F2F] mt-0.5">Motif : {r.rejection_reason}</div>}
            </div>
            {r.status === "pending" && (
              <>
                <Button size="sm" className="h-9 bg-[#0A7D3A] hover:bg-[#086a31]" onClick={() => approve(r.id)} data-testid={`verif-approve-${r.id}`}>Approuver</Button>
                <Button size="sm" variant="outline" className="h-9 text-[#D32F2F]" onClick={() => { setRejecting(r); setReason(""); }} data-testid={`verif-reject-${r.id}`}>Refuser</Button>
              </>
            )}
          </Row>
        ))}
        {items.length === 0 && <div className="py-8 text-center text-sm text-[#73706B]">Aucune demande</div>}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(v) => !v && setRejecting(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>Refuser la demande</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Motif (visible par le marchand)</Label>
            <Input data-testid="verif-reject-reason" className="h-11 rounded-xl" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: documents insuffisants" />
          </div>
          <DialogFooter>
            <Button onClick={reject} className="w-full h-11 rounded-xl bg-[#D32F2F] hover:bg-[#B71C1C]" data-testid="verif-reject-confirm">Confirmer le refus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Phase 4: Advanced search ----------
function AdminSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ shops: [], products: [] });
  const [loading, setLoading] = useState(false);
  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/search", { params: { q: q.trim() } });
      setResults(data || { shops: [], products: [] });
    } catch { toast.error("Erreur de recherche"); }
    setLoading(false);
  };
  const onKey = (e) => { if (e.key === "Enter") search(); };
  return (
    <div>
      <div className="flex gap-2">
        <Input data-testid="admin-search-input" className="h-11 rounded-xl flex-1" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Nom ou ID unique (UUID) — boutique ou produit" />
        <Button onClick={search} disabled={loading} className="h-11 rounded-xl bg-[#E65100] hover:bg-[#C44100]" data-testid="admin-search-btn">{loading ? "..." : "Rechercher"}</Button>
      </div>
      <div className="mt-3 space-y-3">
        {results.shops.length > 0 && <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#73706B] mb-1">Boutiques ({results.shops.length})</div>
          {results.shops.map(s => (
            <Row key={s.id}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm flex items-center gap-1">{s.name}{s.verified && <BadgeCheck className="h-4 w-4 text-[#0A7D3A]" />}</div>
                <div className="text-[11px] text-[#73706B]">ID: {s.id}</div>
                <div className="text-xs text-[#73706B]">{s.city || "—"} · Score {Math.round(s.score || 0)}/1000</div>
              </div>
            </Row>
          ))}
        </div>}
        {results.products.length > 0 && <div>
          <div className="text-xs font-bold uppercase tracking-wider text-[#73706B] mb-1">Produits ({results.products.length})</div>
          {results.products.map(p => (
            <Row key={p.id}>
              <div className="w-12 h-12 rounded bg-[#F3F1EC] overflow-hidden shrink-0">
                {p.images?.[0] && <img src={mediaSrc(p.images[0])} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{p.title}</div>
                <div className="text-[11px] text-[#73706B]">ID: {p.id}</div>
                <div className="text-xs text-[#73706B]">{formatFCFA(p.price)} · Score {Math.round(p.score || 0)}/1000</div>
              </div>
            </Row>
          ))}
        </div>}
        {q && !loading && results.shops.length === 0 && results.products.length === 0 && (
          <div className="py-8 text-center text-sm text-[#73706B]">Aucun résultat</div>
        )}
      </div>
    </div>
  );
}

// ---------- Phase 10: Broadcast ----------
function AdminBroadcast() {
  const [form, setForm] = useState({ title: "", body: "", audience: "all" });
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/broadcasts").then(r => setHistory(r.data || []));
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Titre et message requis"); return; }
    if (!window.confirm(`Envoyer cette notification à : ${form.audience} ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast", form);
      toast.success(`Envoyée à ${data.recipients} utilisateur(s)`);
      setForm({ title: "", body: "", audience: form.audience });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSending(false);
  };

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-2xl border border-[#E8E6E1] bg-white space-y-3">
        <div className="font-[Manrope] font-bold text-[#1A1918]">Nouvelle diffusion · expéditeur affiché : <span className="text-[#E65100]">Forum CI ✓</span></div>
        <div>
          <Label>Titre</Label>
          <Input data-testid="bcast-title" className="h-11 rounded-xl mt-1" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div>
          <Label>Message</Label>
          <Input data-testid="bcast-body" className="h-11 rounded-xl mt-1" value={form.body} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} />
        </div>
        <div>
          <Label>Audience</Label>
          <Select value={form.audience} onValueChange={(v) => setForm(f => ({ ...f, audience: v }))}>
            <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="bcast-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les utilisateurs</SelectItem>
              <SelectItem value="merchants">Marchands uniquement</SelectItem>
              <SelectItem value="clients">Clients uniquement</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={send} disabled={sending} className="h-11 rounded-xl bg-[#E65100] hover:bg-[#C44100] w-full" data-testid="bcast-send">{sending ? "Envoi…" : "Envoyer la diffusion"}</Button>
      </div>

      <div>
        <div className="font-[Manrope] font-bold text-sm text-[#1A1918] mb-2">Historique</div>
        <div className="space-y-2">
          {history.map(b => (
            <Row key={b.id}>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{b.title}</div>
                <div className="text-xs text-[#73706B] truncate">{b.body}</div>
                <div className="text-[11px] text-[#73706B]">{b.audience} · {b.recipients} envoyés · {b.read_count} vus · {new Date(b.created_at).toLocaleString()}</div>
              </div>
            </Row>
          ))}
          {history.length === 0 && <div className="py-6 text-center text-sm text-[#73706B]">Aucune diffusion envoyée</div>}
        </div>
      </div>
    </div>
  );
}

// ---------- Phase 11: Top lists ----------
function AdminTopLists() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/top-lists").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="py-8 text-center text-sm text-[#73706B]">Chargement…</div>;

  return (
    <div className="space-y-6">
      <Section title={`Top boutiques (${data.top_shops.length})`}>
        {data.top_shops.slice(0, 50).map((s, i) => (
          <Row key={s.id}>
            <div className="w-7 h-7 rounded-full bg-[#FFF3E0] text-[#E65100] font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate flex items-center gap-1">{s.name}{s.verified && <BadgeCheck className="h-4 w-4 text-[#0A7D3A]" />}</div>
              <div className="text-[11px] text-[#73706B]">{s.city || "—"} · {s.views || 0} vues · Score {Math.round(s.score || 0)}/1000</div>
            </div>
          </Row>
        ))}
      </Section>
      <Section title={`Top 100 produits — vues`}>
        {data.top_products_views.slice(0, 100).map((p, i) => (
          <Row key={p.id}>
            <div className="w-7 h-7 rounded-full bg-[#FFF3E0] text-[#E65100] font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{p.title}</div>
              <div className="text-[11px] text-[#73706B]">{p.views || 0} vues · {p.favorites_count || 0} ♥</div>
            </div>
          </Row>
        ))}
      </Section>
      <Section title={`Top 100 produits — favoris`}>
        {data.top_products_favorites.slice(0, 100).map((p, i) => (
          <Row key={p.id}>
            <div className="w-7 h-7 rounded-full bg-[#FFF3E0] text-[#E65100] font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{p.title}</div>
              <div className="text-[11px] text-[#73706B]">{p.favorites_count || 0} ♥ · {p.views || 0} vues</div>
            </div>
          </Row>
        ))}
      </Section>
      <Section title={`Top 30 villes`}>
        {data.top_cities.map((c, i) => (
          <Row key={c.city}>
            <div className="w-7 h-7 rounded-full bg-[#FFF3E0] text-[#E65100] font-bold text-xs flex items-center justify-center shrink-0">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{c.city}</div>
              <div className="text-[11px] text-[#73706B]">{c.shops} boutiques · {c.views || 0} vues</div>
            </div>
          </Row>
        ))}
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-[Manrope] font-bold text-sm text-[#1A1918] mb-2">{title}</div>
      <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">{children}</div>
    </div>
  );
}


// ---------- Phase 3 + 6 + 7: Moderation (shops + products + users + messages) ----------
function ModerationPanel() {
  const [tab, setTab] = useState("shops");
  return (
    <div>
      <div className="flex gap-2 mb-3">
        {[["shops", "Boutiques"], ["products", "Produits"], ["users", "Utilisateurs"]].map(([k, lbl]) => (
          <button
            key={k}
            data-testid={`mod-sub-${k}`}
            onClick={() => setTab(k)}
            className={`h-9 px-3 rounded-lg text-xs font-bold transition border ${tab === k ? "bg-[#E65100] text-white border-[#E65100]" : "bg-white border-[#E8E6E1]"}`}
          >{lbl}</button>
        ))}
      </div>
      {tab === "shops" && <ShopsModeration />}
      {tab === "products" && <ProductsModeration />}
      {tab === "users" && <UsersModeration />}
    </div>
  );
}

function ShopsModeration() {
  const [shops, setShops] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/shops").then(r => setShops(r.data || []));
  useEffect(() => { load(); }, []);

  const setVerifLevel = async (shop, level) => {
    try {
      await api.post(`/admin/shops/${shop.id}/verify`, { verified: level >= 3, verification_level: level });
      toast.success(level === 0 ? "Vérification retirée" : `Niveau ${level} appliqué`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const togglePromote = async (shop) => {
    try {
      await api.post(`/admin/shops/${shop.id}/promote`, { promoted: !shop.promoted });
      toast.success(shop.promoted ? "Promotion retirée" : "Boutique promue (boost x2)");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = shops.filter(s => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.id === q);

  return (
    <div>
      <Input data-testid="mod-shops-filter" className="h-10 rounded-xl mb-3" placeholder="Filtrer par nom ou ID…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="space-y-2">
        {filtered.slice(0, 100).map(s => (
          <Row key={s.id}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm flex items-center gap-1 flex-wrap">
                <Link
                  to={`/boutique/${s.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mod-shop-link-${s.id}`}
                  className="text-[#E65100] hover:underline inline-flex items-center gap-1"
                >
                  {s.name}
                  <ExternalLink className="h-3 w-3" />
                </Link>
                {s.verified && <BadgeCheck className="h-4 w-4 text-[#0A7D3A]" />}
                {s.promoted && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFF3E0] text-[#E65100]">PROMUE</span>}
              </div>
              <div className="text-[11px] text-[#73706B] truncate">ID: {s.id}</div>
              <div className="text-xs text-[#73706B]">
                {s.city || "—"} · Niveau vérif {s.verification_level || 0}/3 · Score {Math.round(s.score || 0)}/1000 · {s.views || 0} vues
              </div>
            </div>
            {/* Phase 3: per-level verification toggles */}
            <div className="flex gap-1 flex-wrap justify-end">
              {[0, 1, 2, 3].map(lvl => (
                <button
                  key={lvl}
                  type="button"
                  data-testid={`mod-shop-verif-${s.id}-${lvl}`}
                  onClick={() => setVerifLevel(s, lvl)}
                  className={`h-8 w-8 rounded-lg text-xs font-bold border ${(s.verification_level || 0) === lvl ? "bg-[#0A7D3A] text-white border-[#0A7D3A]" : "bg-white border-[#E8E6E1]"}`}
                  title={`Niveau ${lvl}`}
                >{lvl}</button>
              ))}
            </div>
            {/* Phase 7: 1-click promote */}
            <Button size="sm" variant="outline" onClick={() => togglePromote(s)} data-testid={`mod-shop-promote-${s.id}`} className={`h-9 ${s.promoted ? "text-[#E65100]" : ""}`}>
              <Zap className="h-4 w-4" />
            </Button>
            {/* Phase 6: send admin message */}
            <SendMsgButton userId={s.owner_id} testId={`mod-shop-msg-${s.id}`} />
          </Row>
        ))}
        {filtered.length === 0 && <div className="py-8 text-center text-sm text-[#73706B]">Aucune boutique</div>}
      </div>
    </div>
  );
}

function ProductsModeration() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/products").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const toggleBoost = async (p) => {
    const next = (p.boost_multiplier || 1) > 1 ? 1.0 : 1.5;
    try {
      await api.post(`/admin/products/${p.id}/boost`, { boost_multiplier: next });
      toast.success(next > 1 ? "Boost x1.5 appliqué" : "Boost retiré");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Supprimer le produit « ${p.title} » ?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = items.filter(p => !q || p.title.toLowerCase().includes(q.toLowerCase()) || p.id === q);

  return (
    <div>
      <Input data-testid="mod-products-filter" className="h-10 rounded-xl mb-3" placeholder="Filtrer par titre ou ID…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="space-y-2">
        {filtered.slice(0, 100).map(p => (
          <Row key={p.id}>
            <div className="w-12 h-12 rounded bg-[#F3F1EC] overflow-hidden shrink-0">
              {p.images?.[0] && <img src={mediaSrc(p.images[0])} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate flex items-center gap-2">
                <Link
                  to={`/produit/${p.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mod-prod-link-${p.id}`}
                  className="text-[#E65100] hover:underline inline-flex items-center gap-1 truncate"
                >
                  <span className="truncate">{p.title}</span>
                  <ExternalLink className="h-3 w-3 shrink-0" />
                </Link>
                {(p.boost_multiplier || 1) > 1 && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFF3E0] text-[#E65100]">BOOST x{p.boost_multiplier}</span>}
              </div>
              <div className="text-[11px] text-[#73706B] truncate">ID: {p.id}</div>
              <div className="text-xs text-[#73706B]">{formatFCFA(p.price)} · {p.views || 0} vues · Score {Math.round(p.score || 0)}/1000</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => toggleBoost(p)} data-testid={`mod-prod-boost-${p.id}`} className={`h-9 ${(p.boost_multiplier || 1) > 1 ? "text-[#E65100]" : ""}`}>
              <Zap className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => remove(p)} data-testid={`mod-prod-del-${p.id}`} className="h-9 text-[#D32F2F]">
              <Trash2 className="h-4 w-4" />
            </Button>
          </Row>
        ))}
        {filtered.length === 0 && <div className="py-8 text-center text-sm text-[#73706B]">Aucun produit</div>}
      </div>
    </div>
  );
}

function UsersModeration() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/users").then(r => setUsers(r.data || []));
  useEffect(() => { load(); }, []);

  const toggleBan = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/ban`, { banned: !u.banned });
      toast.success(u.banned ? "Débanni" : "Banni");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filtered = users.filter(u => !q ||
    (u.email || "").toLowerCase().includes(q.toLowerCase()) ||
    (u.first_name || "").toLowerCase().includes(q.toLowerCase()) ||
    u.id === q);

  return (
    <div>
      <Input data-testid="mod-users-filter" className="h-10 rounded-xl mb-3" placeholder="Filtrer par email, nom ou ID…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="space-y-2">
        {filtered.slice(0, 100).map(u => (
          <Row key={u.id}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate flex items-center gap-2">
                {(u.first_name || "") + " " + (u.last_name || "")}
                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#F3F1EC] text-[#73706B]">{u.role}</span>
                {u.banned && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFEAEA] text-[#D32F2F]">BANNI</span>}
              </div>
              <div className="text-[11px] text-[#73706B] truncate">ID: {u.id}</div>
              <div className="text-xs text-[#73706B]">{u.email || "—"} · {u.phone || "—"}</div>
            </div>
            <SendMsgButton userId={u.id} testId={`mod-user-msg-${u.id}`} />
            <Button size="sm" variant="outline" onClick={() => toggleBan(u)} data-testid={`mod-user-ban-${u.id}`} className={`h-9 ${u.banned ? "" : "text-[#D32F2F]"}`}>
              {u.banned ? "Débannir" : "Bannir"}
            </Button>
          </Row>
        ))}
        {filtered.length === 0 && <div className="py-8 text-center text-sm text-[#73706B]">Aucun utilisateur</div>}
      </div>
    </div>
  );
}

// Reusable: send admin message dialog opener
function SendMsgButton({ userId, testId }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  if (!userId) return null;
  const send = async () => {
    if (!body.trim()) { toast.error("Message requis"); return; }
    setSending(true);
    try {
      await api.post("/admin/messages", { user_id: userId, body });
      toast.success("Message envoyé");
      setOpen(false); setBody("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    setSending(false);
  };
  return (
    <>
      <Button size="sm" variant="outline" className="h-9" onClick={() => setOpen(true)} data-testid={testId} title="Envoyer un message"><MessageCircle className="h-4 w-4" /></Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Message Forum CI ✓ → utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Message (lecture seule côté utilisateur)</Label>
            <Input data-testid="mod-msg-body" className="h-11 rounded-xl" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Votre message…" />
            <div className="text-[11px] text-[#73706B]">L'utilisateur ne peut pas répondre. Vous pouvez clore la conversation à tout moment.</div>
          </div>
          <DialogFooter>
            <Button onClick={send} disabled={sending} className="w-full h-11 rounded-xl bg-[#E65100] hover:bg-[#C44100]" data-testid="mod-msg-send">{sending ? "Envoi…" : "Envoyer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

